import React, { type FC } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import AddOutlined from '@mui/icons-material/AddOutlined'
import SaveAltOutlined from '@mui/icons-material/SaveAltOutlined'
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import PowerSettingsNew from '@mui/icons-material/PowerSettingsNew'
import SaveOutlined from '@mui/icons-material/SaveOutlined'
import { LongPressIconButton, OrderByControl, standardTooltipProps } from '@/common/components'
import type { SortOption } from '@/common/components'
import { useDialog, useSortOrderState } from '@/common/hooks'
import { usePendingSessionTarget, useSessionDialogManager } from '@/features/inbox'
import { ErrorBoundary } from '@/infra/error'
import { AuthLoadingBoundary } from '@/infra/auth'
import { useSessionStateContext } from '@/features/events/conversation/state'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import { hasAnyDrafts } from '@/features/events/conversation/state/types'
import { SessionTree } from '@/features/events/conversation/tree'
import type { TreeConfig, TreeSortOrder } from '@/features/events/conversation/tree'
import { ConnectedHostStatusIcon, ConnectedSessionIcon, useStatusContext } from '@/features/status'
import { SessionStatus } from '@/infra/sse/status'
import type { ParkedItems } from '@/features/events/shared/hooks'
import {
  useParkedItems,
  useSessionStatusBuckets,
  useTransitioningSessionsState,
} from '@/features/events/shared/hooks'
import type { SessionStatusTab } from '@/features/events/shared/types'
import {
  DEFAULT_SESSION_STATUS_TAB,
  SESSION_STATUS_TAB_VALUES,
} from '@/features/events/shared/types'
import {
  useArchiveSession,
  useResumeSession,
  useTerminateSession,
} from '@/features/events/conversation/controls'
import { UserInputDialog } from '@/features/events/conversation/dialogs'
import { HostStartSessionModal, StartSessionModal } from '@/features/events/start-session'
import type { UserHostDetails } from '@/infra/sse/shared/payloads'
import { DRAFT_DIALOG_CONFIG } from '@/features/events/pages/shared/constants/draftDialogConfig'
import { DeleteAllConfirmationDialog, SessionEventsOrchestrator } from './components'

const RESUME_DIALOG_CONFIG = DRAFT_DIALOG_CONFIG['resume_session']

const UnknownToolsCollector = import.meta.env.DEV
  ? (await import('@dev-tools/UnknownToolsCollector')).UnknownToolsCollector
  : () => null

const EVENTS_SORT_STORAGE_KEY = 'events-sort-order'

const EVENTS_SORT_OPTIONS: readonly SortOption<TreeSortOrder>[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'alpha-asc', label: 'A \u2192 Z' },
  { value: 'alpha-desc', label: 'Z \u2192 A' },
] as const

const VALID_EVENTS_SORT_VALUES: readonly TreeSortOrder[] = [
  'newest',
  'oldest',
  'alpha-asc',
  'alpha-desc',
]

const PARK_ACTION_COLOR = 'secondary.light'

const TAB_EMPTY_MESSAGES: Record<
  SessionStatusTab,
  { readonly message: string; readonly hint: string }
> = {
  active: {
    message: 'No active sessions',
    hint: 'Sessions appear here while Claude Code is running',
  },
  resumable: {
    message: 'No resumable sessions',
    hint: 'Sessions that can be resumed will appear here',
  },
  parked: { message: 'No parked items', hint: 'Parked hosts, projects, and sessions appear here' },
}

const TAB_LABELS: Record<SessionStatusTab, string> = {
  active: 'Active',
  resumable: 'Resumable',
  parked: 'Parked',
}

function renderHostIcon(hostname: string) {
  return <ConnectedHostStatusIcon hostname={hostname} />
}

function renderSessionIcon(session: ClaudeSession) {
  return <ConnectedSessionIcon session={session} />
}

const EMPTY_STATE_MESSAGES = [
  'All clear',
  'All caught up',
  'Inbox zero achieved',
  'Clean slate',
  'No hooks in sight',
  'Hook-free zone',
  'Radio silence',
  'Peaceful waters',
  'Serene state',
  'Crickets',
] as const

interface StartSessionTarget {
  readonly projectKey: string
  readonly userHostDetails: UserHostDetails
}

interface HostStartSessionTarget {
  readonly hostname: string
  readonly hostId: string
  readonly userHostDetails: UserHostDetails
}

interface ArchiveTarget {
  readonly session: ClaudeSession
  readonly sessionName: string
}

interface ArchiveDialogState {
  readonly isOpen: boolean
  readonly target: ArchiveTarget | null
}

const INITIAL_ARCHIVE_STATE: ArchiveDialogState = {
  isOpen: false,
  target: null,
}

export const EventsPage: FC = () => {
  const { sessions, recordCommand, createNewTurn, deleteSessions } = useSessionStateContext()

  const {
    parkedItems,
    parkHost,
    parkProject,
    parkSession,
    unparkHost,
    unparkProject,
    unparkSession,
  } = useParkedItems()

  const [sortOrder, setSortOrder] = useSortOrderState<TreeSortOrder>(
    EVENTS_SORT_STORAGE_KEY,
    'newest',
    VALID_EVENTS_SORT_VALUES,
  )
  const [activeTab, setActiveTab] = useState<SessionStatusTab>(DEFAULT_SESSION_STATUS_TAB)

  const { getSessionStatus, isLoading: isStatusLoading } = useStatusContext()
  const { buckets, counts } = useSessionStatusBuckets(sessions, getSessionStatus, parkedItems)

  const pendingTarget = usePendingSessionTarget()
  const { openSessionDrafts } = useSessionDialogManager()

  const visibleTabs = useMemo(
    () => SESSION_STATUS_TAB_VALUES.filter((tab) => counts[tab] > 0),
    [counts],
  )

  const effectiveTab = visibleTabs.includes(activeTab) ? activeTab : (visibleTabs[0] ?? 'active')

  useEffect(() => {
    if (isStatusLoading || visibleTabs.length === 0) return
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] ?? DEFAULT_SESSION_STATUS_TAB)
    }
  }, [isStatusLoading, visibleTabs, activeTab, setActiveTab])

  useEffect(() => {
    if (pendingTarget === null) return

    const targetTab = SESSION_STATUS_TAB_VALUES.find((tab) =>
      buckets[tab].some((session) => session.sessionId === pendingTarget),
    )

    if (targetTab) {
      setActiveTab(targetTab)
    }
  }, [pendingTarget, buckets, setActiveTab])

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: SessionStatusTab) => {
      setActiveTab(newValue)
    },
    [setActiveTab],
  )

  const filteredSessions = isStatusLoading ? sessions : buckets[effectiveTab]

  const [archiveState, setArchiveState] = useState<ArchiveDialogState>(INITIAL_ARCHIVE_STATE)
  const [startSessionTarget, setStartSessionTarget] = useState<StartSessionTarget | null>(null)
  const [hostStartSessionTarget, setHostStartSessionTarget] =
    useState<HostStartSessionTarget | null>(null)

  const transitioningState = useTransitioningSessionsState()

  const resumeDialog = useDialog()
  const resumeTargetRef = useRef<ClaudeSession | null>(null)

  const handleResumeSessionClick = useCallback(
    (session: ClaudeSession) => {
      resumeTargetRef.current = session
      setResumeError(null)
      resumeDialog.open()
    },
    [resumeDialog],
  )

  const handleResumeDialogClose = useCallback(() => {
    resumeDialog.close()
    resumeTargetRef.current = null
  }, [resumeDialog])

  const { resumeSession, isSubmitting: isResumeSubmitting } = useResumeSession()
  const { terminateSession } = useTerminateSession()
  const { archiveSession } = useArchiveSession()
  const [resumeError, setResumeError] = useState<string | null>(null)

  const handleUnparkSession = useCallback(
    (sessionId: string) => {
      const session = sessions.find((s) => s.sessionId === sessionId)
      if (!session) return

      promoteSessionUnpark(session, sessionId, sessions, parkedItems, {
        unparkHost,
        unparkProject,
        unparkSession,
        parkSession,
      })
    },
    [sessions, parkedItems, unparkHost, unparkProject, unparkSession, parkSession],
  )

  const handleResumeSubmit = useCallback(
    async (prompt: string): Promise<boolean> => {
      const target = resumeTargetRef.current
      if (!target) return false

      setResumeError(null)

      try {
        const initialMessage = prompt.trim() || null
        const success = await resumeSession(target, initialMessage)
        if (success) {
          transitioningState.markTransitioning(target.sessionId, 'resuming')
          handleUnparkSession(target.sessionId)
          handleResumeDialogClose()
        }
        return success
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred'
        setResumeError(message)
        return false
      }
    },
    [resumeSession, transitioningState, handleUnparkSession, handleResumeDialogClose],
  )

  const handleTerminateSession = useCallback(
    async (session: ClaudeSession) => {
      try {
        const success = await terminateSession(session)
        if (success) {
          transitioningState.markTransitioning(session.sessionId, 'terminating')
        }
      } catch {
        // Terminate failed — no flash needed
      }
    },
    [terminateSession, transitioningState],
  )

  const eventsTreeConfig: TreeConfig = useMemo(
    () => ({
      icons: {
        host: renderHostIcon,
        project: <FolderOutlined fontSize="small" />,
        session: renderSessionIcon,
      },
      expandTogglePosition: 'left',
      persistExpansion: true,
      storageKeyPrefix: 'events-tree',
      defaultExpanded: true,
      showCounts: false,
      sortOrder,
    }),
    [sortOrder],
  )

  const openArchiveDialog = useCallback((target: ArchiveTarget) => {
    setArchiveState({ isOpen: true, target })
  }, [])

  const closeArchiveDialog = useCallback(() => {
    setArchiveState(INITIAL_ARCHIVE_STATE)
  }, [])

  const handleConfirmArchive = useCallback(async () => {
    const target = archiveState.target
    if (!target) return

    const success = await archiveSession(target.session)
    if (success) {
      deleteSessions([target.session.sessionId])
    } else {
      throw new Error('Archive failed')
    }
  }, [archiveState.target, deleteSessions, archiveSession])

  const archiveDialogConfig = useMemo(() => {
    if (!archiveState.target) return null

    return {
      title: 'Archive session',
      message: <SessionArchiveMessage sessionName={archiveState.target.sessionName} />,
      confirmText: 'Archive',
      isDestructive: false,
    }
  }, [archiveState.target])

  const handleSessionArchiveLongPress = useCallback(
    (session: ClaudeSession) => {
      const sessionName = session.sessionName || `Session ${session.sessionId.substring(0, 8)}`
      openArchiveDialog({ session, sessionName })
    },
    [openArchiveDialog],
  )

  const handleStartSessionClick = useCallback(
    (projectKey: string, projectSessions: readonly ClaudeSession[]) => {
      const firstSession = projectSessions[0]
      if (!firstSession) return
      setStartSessionTarget({
        projectKey,
        userHostDetails: firstSession.userHostDetails,
      })
    },
    [],
  )

  const handleStartSessionClose = useCallback(() => {
    setStartSessionTarget(null)
  }, [])

  const handleHostStartSessionClick = useCallback(
    (hostname: string, hostSessions: readonly ClaudeSession[]) => {
      const firstSession = hostSessions[0]
      if (!firstSession) return
      setHostStartSessionTarget({
        hostname,
        hostId: firstSession.userHostDetails.hostId,
        userHostDetails: firstSession.userHostDetails,
      })
    },
    [],
  )

  const handleHostStartSessionClose = useCallback(() => {
    setHostStartSessionTarget(null)
  }, [])

  const handleUnparkProject = useCallback(
    (projectKey: string) => {
      const sessionsInProject = sessions.filter((s) => s.contextWorkDirectory === projectKey)
      const hostname = sessionsInProject[0]?.userHostDetails.hostName

      if (hostname && parkedItems.hosts.has(hostname)) {
        unparkHost(hostname)
        for (const s of sessions) {
          if (s.userHostDetails.hostName === hostname && s.contextWorkDirectory !== projectKey) {
            parkProject(s.contextWorkDirectory)
          }
        }
      } else {
        unparkProject(projectKey)
      }

      for (const s of sessionsInProject) {
        unparkSession(s.sessionId)
      }
    },
    [sessions, parkedItems, unparkHost, unparkProject, unparkSession, parkProject],
  )

  const handleUnparkHost = useCallback(
    (hostname: string) => {
      unparkHost(hostname)
      for (const s of sessions) {
        if (s.userHostDetails.hostName === hostname) {
          unparkProject(s.contextWorkDirectory)
          unparkSession(s.sessionId)
        }
      }
    },
    [sessions, unparkHost, unparkProject, unparkSession],
  )

  const isParkedTab = effectiveTab === 'parked'

  const sessionContent = useMemo(() => {
    if (sessions.length === 0) return <EmptyState />
    if (filteredSessions.length === 0) return <TabEmptyState tab={effectiveTab} />

    return (
      <SessionTree
        sessions={filteredSessions}
        config={eventsTreeConfig}
        actionSlots={{
          host: (hostname, hostSessions) =>
            isParkedTab
              ? [
                  <LongPressIconButton
                    key="unpark-host"
                    icon={<VisibilityOutlined sx={actionIconSx} />}
                    onLongPress={() => handleUnparkHost(hostname)}
                    tooltipText="Hold to unpark this host"
                    color="success.main"
                  />,
                ]
              : [
                  ...(hostSessions.length > 0
                    ? [
                        <Tooltip
                          {...standardTooltipProps}
                          key="start-session-host"
                          title="Start new session on this host"
                        >
                          <IconButton
                            size="small"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation()
                              handleHostStartSessionClick(hostname, hostSessions)
                            }}
                            sx={startSessionIconSx}
                            aria-label={`Start new session on ${hostname}`}
                          >
                            <AddOutlined sx={startSessionIconSizeSx} />
                          </IconButton>
                        </Tooltip>,
                      ]
                    : []),
                  <LongPressIconButton
                    key="park-host"
                    icon={<VisibilityOffOutlined sx={actionIconSx} />}
                    onLongPress={() => parkHost(hostname)}
                    tooltipText="Hold to park this host"
                    color={PARK_ACTION_COLOR}
                  />,
                ],
          project: (projectKey, projectSessions) =>
            isParkedTab
              ? [
                  <LongPressIconButton
                    key="unpark-project"
                    icon={<VisibilityOutlined sx={actionIconSx} />}
                    onLongPress={() => handleUnparkProject(projectKey)}
                    tooltipText="Hold to unpark this project"
                    color="success.main"
                  />,
                ]
              : [
                  <Tooltip {...standardTooltipProps} key="start-session" title="Start new session">
                    <IconButton
                      size="small"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        handleStartSessionClick(projectKey, projectSessions)
                      }}
                      sx={startSessionIconSx}
                      aria-label="Start new session in this project"
                    >
                      <AddOutlined sx={startSessionIconSizeSx} />
                    </IconButton>
                  </Tooltip>,
                  <LongPressIconButton
                    key="park-project"
                    icon={<VisibilityOffOutlined sx={actionIconSx} />}
                    onLongPress={() => parkProject(projectKey)}
                    tooltipText="Hold to park this project"
                    color={PARK_ACTION_COLOR}
                  />,
                ],
          session: (session) => {
            const status = getSessionStatus(session.sessionId)
            const isRunning = status === SessionStatus.RUNNING
            const isResumable = status === SessionStatus.RESUMABLE
            const commonActions = [
              ...(isRunning
                ? [
                    <LongPressIconButton
                      key="terminate-session"
                      icon={<PowerSettingsNew sx={actionIconSx} />}
                      onLongPress={() => handleTerminateSession(session)}
                      tooltipText="Hold to terminate this session"
                      disabled={transitioningState.isTransitioning(session.sessionId)}
                      color="warning.main"
                      progressColor="warning.main"
                    />,
                  ]
                : []),
              ...(isResumable
                ? [
                    <Tooltip {...standardTooltipProps} key="resume-session" title="Resume session">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            handleResumeSessionClick(session)
                          }}
                          sx={resumeActionIconSx}
                          aria-label="Resume session"
                          disabled={transitioningState.isTransitioning(session.sessionId)}
                        >
                          <PowerSettingsNew sx={resumePlayIconSx} />
                        </IconButton>
                      </span>
                    </Tooltip>,
                  ]
                : []),
              <LongPressIconButton
                key="archive-session"
                icon={<SaveAltOutlined sx={actionIconSx} />}
                onLongPress={() => handleSessionArchiveLongPress(session)}
                tooltipText="Hold to archive this session"
              />,
            ]
            const parkToggle = isParkedTab ? (
              <LongPressIconButton
                key="unpark-session"
                icon={<VisibilityOutlined sx={actionIconSx} />}
                onLongPress={() => handleUnparkSession(session.sessionId)}
                tooltipText="Hold to unpark this session"
                color="success.main"
              />
            ) : (
              <LongPressIconButton
                key="park-session"
                icon={<VisibilityOffOutlined sx={actionIconSx} />}
                onLongPress={() => parkSession(session.sessionId)}
                tooltipText="Hold to park this session"
                color={PARK_ACTION_COLOR}
              />
            )
            return [...commonActions, parkToggle]
          },
          persistentSession: (session) =>
            hasAnyDrafts(session)
              ? [
                  <Tooltip {...standardTooltipProps} key="view-drafts" title="View drafts">
                    <IconButton
                      size="small"
                      aria-label="View drafts"
                      sx={draftActionIconSx}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        openSessionDrafts(session.sessionId)
                      }}
                    >
                      <SaveOutlined sx={actionIconSx} />
                    </IconButton>
                  </Tooltip>,
                ]
              : [],
        }}
        renderSessionOrchestrator={(session) => {
          const sessionStatus = getSessionStatus(session.sessionId)
          const isResumable = sessionStatus === SessionStatus.RESUMABLE
          const transitionType = transitioningState.getTransitionType(session.sessionId)
          return (
            <SessionEventsOrchestrator
              session={session}
              recordCommand={recordCommand}
              createNewTurn={createNewTurn}
              isTransitioning={transitioningState.isTransitioning(session.sessionId)}
              transitionType={transitionType}
              onResumeSession={isResumable ? handleResumeSessionClick : undefined}
            />
          )
        }}
      />
    )
  }, [
    sessions.length,
    filteredSessions,
    effectiveTab,
    isParkedTab,
    eventsTreeConfig,
    parkHost,
    parkProject,
    parkSession,
    handleUnparkHost,
    handleUnparkProject,
    handleUnparkSession,
    handleSessionArchiveLongPress,
    handleStartSessionClick,
    handleHostStartSessionClick,
    recordCommand,
    createNewTurn,
    transitioningState,
    handleResumeSessionClick,
    getSessionStatus,
    handleTerminateSession,
    openSessionDrafts,
  ])

  return (
    <Box sx={contentWrapperSx}>
      <Box sx={headerSx}>
        <Box component="h1" sx={titleSx}>
          Events
        </Box>
      </Box>

      {sessions.length > 0 && (
        <Box sx={controlsRowSx}>
          {!isStatusLoading && (
            <Tabs
              value={effectiveTab}
              onChange={handleTabChange}
              aria-label="Filter sessions by status"
              sx={statusTabsSx}
            >
              {visibleTabs.map((tab) => (
                <Tab
                  key={tab}
                  value={tab}
                  label={`${TAB_LABELS[tab]} (${counts[tab]})`}
                  sx={statusTabSx}
                />
              ))}
            </Tabs>
          )}
          <OrderByControl<TreeSortOrder>
            options={EVENTS_SORT_OPTIONS}
            value={sortOrder}
            onChange={setSortOrder}
          />
        </Box>
      )}

      <Box data-testid="host-events-content" sx={mainAreaSx}>
        <UnknownToolsCollector />
        <AuthLoadingBoundary>
          <ErrorBoundary>{sessionContent}</ErrorBoundary>
        </AuthLoadingBoundary>
      </Box>

      {archiveDialogConfig && (
        <DeleteAllConfirmationDialog
          open={archiveState.isOpen}
          onClose={closeArchiveDialog}
          onConfirm={handleConfirmArchive}
          title={archiveDialogConfig.title}
          confirmText={archiveDialogConfig.confirmText}
          cancelText="Cancel"
          isDestructive={archiveDialogConfig.isDestructive}
        >
          {archiveDialogConfig.message}
        </DeleteAllConfirmationDialog>
      )}

      {resumeTargetRef.current && (
        <UserInputDialog
          open={resumeDialog.isOpen}
          onClose={handleResumeDialogClose}
          onSubmit={handleResumeSubmit}
          session={resumeTargetRef.current}
          isSubmitting={isResumeSubmitting}
          error={resumeError}
          draftType="resume_session"
          refEventId={null}
          title={RESUME_DIALOG_CONFIG.title}
          placeholder={RESUME_DIALOG_CONFIG.placeholder}
          description={RESUME_DIALOG_CONFIG.description}
          submitButtonLabel={RESUME_DIALOG_CONFIG.submitButtonLabel}
          submitButtonLoadingLabel={RESUME_DIALOG_CONFIG.submitButtonLoadingLabel}
          agentModeSelectable={false}
          allowEmptySubmit={true}
          hideAgentMode={true}
          submitButtonColor="warning"
        />
      )}

      {startSessionTarget !== null && (
        <StartSessionModal
          onClose={handleStartSessionClose}
          projectKey={startSessionTarget.projectKey}
          userHostDetails={startSessionTarget.userHostDetails}
        />
      )}

      {hostStartSessionTarget !== null && (
        <HostStartSessionModal
          hostname={hostStartSessionTarget.hostname}
          hostId={hostStartSessionTarget.hostId}
          userHostDetails={hostStartSessionTarget.userHostDetails}
          onClose={handleHostStartSessionClose}
        />
      )}
    </Box>
  )
}

interface UnparkActions {
  readonly unparkHost: (hostname: string) => void
  readonly unparkProject: (projectKey: string) => void
  readonly unparkSession: (sessionId: string) => void
  readonly parkSession: (sessionId: string) => void
}

function promoteSessionUnpark(
  session: ClaudeSession,
  sessionId: string,
  sessions: readonly ClaudeSession[],
  parkedItems: ParkedItems,
  actions: UnparkActions,
): void {
  const hostname = session.userHostDetails.hostName
  const projectKey = session.contextWorkDirectory

  if (parkedItems.hosts.has(hostname)) {
    actions.unparkHost(hostname)
    for (const s of sessions) {
      if (s.userHostDetails.hostName === hostname && s.sessionId !== sessionId) {
        actions.parkSession(s.sessionId)
      }
    }
    return
  }

  if (parkedItems.projects.has(projectKey)) {
    actions.unparkProject(projectKey)
    for (const s of sessions) {
      if (s.contextWorkDirectory === projectKey && s.sessionId !== sessionId) {
        actions.parkSession(s.sessionId)
      }
    }
    return
  }

  actions.unparkSession(sessionId)
}

interface SessionArchiveMessageProps {
  readonly sessionName: string
}

const SessionArchiveMessage: FC<SessionArchiveMessageProps> = ({ sessionName }) => (
  <Typography variant="body2" sx={deletionMessageSx}>
    This will archive the session{' '}
    <Box component="span" sx={deletionMessageHighlightSx}>
      {sessionName}
    </Box>{' '}
    and remove it from the active view.
  </Typography>
)

const EmptyState: FC = () => {
  const message = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * EMPTY_STATE_MESSAGES.length)
    return EMPTY_STATE_MESSAGES[randomIndex] ?? 'All clear'
  }, [])

  return (
    <Box sx={emptyStateSx}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={emptyStateHintSx}>
        Waiting for Claude Code activity
      </Typography>
    </Box>
  )
}

interface TabEmptyStateProps {
  readonly tab: SessionStatusTab
}

const TabEmptyState: FC<TabEmptyStateProps> = ({ tab }) => {
  const { message, hint } = TAB_EMPTY_MESSAGES[tab]
  return (
    <Box sx={emptyStateSx}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={emptyStateHintSx}>
        {hint}
      </Typography>
    </Box>
  )
}

const contentWrapperSx: SxProps<Theme> = {
  padding: 4,
  paddingTop: 6,
}

const headerSx: SxProps<Theme> = {
  textAlign: 'center',
  mb: 6,
  // Sit above the controls row's upward mask (z:10) so the title stays visible
  // at rest; once scrolled, the header leaves the viewport and the mask takes over.
  position: 'relative',
  zIndex: 11,
  backgroundColor: 'background.default',
}

const titleSx: SxProps<Theme> = {
  fontSize: '1.75rem',
  fontWeight: 400,
  color: 'text.primary',
  mb: 1,
  letterSpacing: '0.01em',
}

const mainAreaSx: SxProps<Theme> = {
  maxWidth: '1200px',
  mx: 'auto',
}

// Sticky so the controls/sort row never scrolls into the pinned Blocking
// Events HUD overlay (fixed at top: 22, ~88px tall band). top: 96 floors the
// row just below that band with a small gap, regardless of scroll position.
// The backdrop masks page content scrolling underneath. 96px is a modest
// constant — it does not read as dead space when the HUD is absent.
const CONTROLS_ROW_STICKY_TOP_PX = 96

const controlsRowSx: SxProps<Theme> = {
  maxWidth: '1200px',
  mx: 'auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'sticky',
  top: `${CONTROLS_ROW_STICKY_TOP_PX}px`,
  // Above in-tree content (code blocks sit at z:1) so the opaque bar reliably
  // masks the session tree scrolling beneath it; well below the HUD (1350).
  zIndex: 10,
  backgroundColor: 'background.default',
  // Extend the opaque band upward to the viewport top. Because the bar floors at
  // top: 96 (not 0), content scrolling past it would otherwise render in the 96px
  // gap ABOVE the stuck bar — this pseudo-element masks that gap so nothing leaks.
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '100%',
    height: `${CONTROLS_ROW_STICKY_TOP_PX}px`,
    backgroundColor: 'background.default',
  },
  // Padded opaque band: gives the emerging content breathing room below the
  // stuck bar instead of crowding right against the tabs.
  py: 1.5,
  mb: 1,
}

const statusTabsSx: SxProps<Theme> = {
  minHeight: 36,
  '& .MuiTabs-indicator': {
    height: 2,
  },
}

const statusTabSx: SxProps<Theme> = {
  minHeight: 36,
  minWidth: 'auto',
  px: 2,
  py: 0.5,
  fontSize: '0.8125rem',
  textTransform: 'none',
  fontWeight: 500,
}

const actionIconSx: SxProps<Theme> = {
  fontSize: '1.2rem',
}

// text.primary renders white in this dashboard's dark theme while staying
// theme-adaptive; padding matches the other action-row icon buttons.
const draftActionIconSx: SxProps<Theme> = {
  color: 'text.primary',
  padding: '2px',
}

const startSessionIconSx: SxProps<Theme> = {
  padding: '2px',
  color: 'text.secondary',
  transition: 'color 0.2s ease',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'info.main',
  },
}

const startSessionIconSizeSx: SxProps<Theme> = {
  fontSize: '1.2rem',
}

const resumePlayIconSx: SxProps<Theme> = {
  fontSize: '1.2rem',
}

const resumeActionIconSx: SxProps<Theme> = {
  padding: '2px',
  color: 'text.secondary',
  transition: 'color 0.2s ease',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'success.main',
  },
  '&.Mui-disabled': {
    color: 'action.disabled',
  },
}

const deletionMessageSx: SxProps<Theme> = {
  color: 'text.secondary',
  lineHeight: 1.5,
}

const deletionMessageHighlightSx: SxProps<Theme> = {
  color: 'primary.main',
  fontWeight: 600,
}

const emptyStateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: 8,
}

const emptyStateHintSx: SxProps<Theme> = {
  mt: 1,
}
